import { useQuery, useQueryCallback } from '@tv2/lib-graphql';

import { useMemo } from 'react';
import { RawTrackingData } from '@tv2/lib-offspring/types/LinkType';
import { usePathOrDefault } from '@tv2/lib-offspring/hooks/usePath';
import { mapOvpPathToUrlPath } from '@tv2/lib-offspring/libs/ovpPath';
import { rawToReady } from '../../libs/tracking/applyTracking';
import { FooterDocument } from './__generated__/Footer';
import { FooterMutationDocument, FooterMutationVariables } from './__generated__/FooterMutation';

export type FooterType = {
  id: string;
  categories: {
    title: string;
    items: FooterItem[];
  }[];
};

type FooterItem = {
  title: string;
  url: string;
  tracking?: RawTrackingData;
};

const footer_default_id = 'footer_default';
export function useFooter() {
  const { data, fetching } = useQuery({
    requestPolicy: 'cache-first',
    query: FooterDocument,
    variables: {
      id: footer_default_id,
    },
  });
  const currentPath = usePathOrDefault();

  const saveFooter = useQueryCallback({
    query: FooterMutationDocument,
  });
  const mappedFooter = useMemo(() => {
    return {
      id: data?.playFooter?.id ?? '',
      categories:
        data?.playFooter?.categories?.map(category => ({
          title: category?.title ?? '',
          items: category?.items?.map(item => mapCategoryItem(item, currentPath)) ?? [],
        })) ?? [],
    };
  }, [data, currentPath]);

  return useMemo(
    () => ({
      fetching,
      footer: mappedFooter,
      saveFooter: (footerInput: FooterType) => {
        const variables: FooterMutationVariables = {
          playFooter: {
            ...footerInput,
            id: footer_default_id,
            categories: footerInput.categories.map(category => ({
              ...category,
              items: category.items.map(({ tracking, ...rest }) => rest),
            })),
          },
        };
        return saveFooter(variables);
      },
    }),
    [fetching, mappedFooter, saveFooter]
  );
}

function mapCategoryItem(item: FooterItem, currentPath: string) {
  const _path = mapOvpPathToUrlPath(item.url ?? '/');
  if (isExternalLink(item.url ?? '')) {
    return {
      title: item.title ?? '',
      url: _path ?? '',
    };
  }

  const raw = rawFooterTrackingData(item?.title);
  const ready = rawToReady(
    {
      link: {
        path: _path,
        title: item.title ?? '',
        tracking: raw,
      },
    },
    -1,
    -1,
    currentPath ?? ''
  );
  return {
    title: ready.link.title,
    url: ready.link.path,
    tracking: ready.link.tracking,
  };
}

function rawFooterTrackingData(title: string | null): RawTrackingData {
  return {
    status: 'raw',
    deckId: 'undefined',
    structureType: 'footersection',
    structureTitle: 'undefined',
    teaserType: 'page',
    presentationTitle: title ?? '',
    label: null,
  };
}

function isExternalLink(url: string): boolean {
  return url.includes('http');
}
